import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Check, X, Crown } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import UpgradeToPremium, { PRICING } from '../components/UpgradeToPremium'
import MarketingHeader from '../components/MarketingHeader'
import MarketingFooter from '../components/MarketingFooter'

const FREE_FEATURES = [
  'Guest portal & personal wedding link',
  '1 event with programme management',
  'Up to 5 guests with WhatsApp RSVP links',
  'Up to 5 registry items',
  'Love story & song',
  'Studio photo gallery (5 photos)',
  'Countdown page',
]
const FREE_LOCKED = [
  'Unlimited events, guests & registry items',
  'Budget tracker',
  'Aso Ebi Hub',
  'Vow writer',
  'Wedding Day command centre',
  'Live streaming for diaspora',
  'Coordinator access',
  'Guest photo wall (Through Your Eyes)',
  'Group gifting in registry',
]
const PREM_FEATURES = [
  'Everything in free, no limits',
  'Unlimited events, guests & registry items',
  'Budget tracker — categories, vendors, CSV export',
  'Aso Ebi Hub — orders, payments, lookbook, collection points',
  'Vow writer — private, shared when you are ready',
  'Wedding Day command centre & live check-in',
  'Live stream link for diaspora guests',
  'Coordinator access — no login needed',
  'Guest photo wall (Through Your Eyes)',
  'Group gifting & gift log with thank-you messages',
]

const PRICING_FAQS = [
  { q:'Is Okoiya really free to start?', a:'Yes — sign up, create your wedding, add one event, manage up to 5 guests, add up to 5 registry items, upload 5 studio photos, and publish your guest portal completely free. No credit card required. Upgrade to Premium any time to remove all limits and unlock every feature.' },
  { q:'What happens on the wedding day?', a:'Premium couples get a live Guest Check-In page where an usher can search and mark guests as arrived per event. You also get a live streaming link for guests watching remotely.' },
  { q:'What currency does the budget tracker support?', a:'NGN (₦), USD ($), GBP (£), EUR (€), GHS (₵), KES (KSh), and ZAR (R). You can switch currency at any time.' },
  { q:'Can I export my guest list and budget?', a:'Yes - both can be exported as CSV from their respective pages. The programme can be downloaded as a PDF. These work on free and premium plans.' },
]

function FAQItem({ faq, isOpen, onToggle }) {
  return (
    <div style={{ background:'white', border:'1px solid #E5E0D8', borderRadius:'1rem', overflow:'hidden' }}>
      <button onClick={onToggle} style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'1.125rem 1.375rem', background:'none', border:'none', cursor:'pointer', textAlign:'left', gap:'1rem' }}>
        <span style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:600, fontSize:'0.95rem', color:'#1A1A1A', flex:1 }}>{faq.q}</span>
        <span style={{ fontSize:'1.25rem', color:'#C4826A', transition:'transform 0.2s', display:'inline-block', transform:isOpen?'rotate(45deg)':'none' }}>+</span>
      </button>
      {isOpen && (
        <div style={{ padding:'0 1.375rem 1.125rem', borderTop:'1px solid #F3F0EA' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', color:'#6B6B6B', lineHeight:1.75, margin:'0.75rem 0 0' }}>{faq.a}</p>
        </div>
      )}
    </div>
  )
}

import { useSeo } from '../lib/seo'

export default function PricingPage() {
  useSeo({ title: 'Pricing', description: 'Start free, upgrade when you need more. Simple Okoiya pricing for couples planning their wedding — premium unlocks unlimited events, guests, budget tracking and more.', path: '/pricing' })
  const { user } = useAuth()
  const navigate  = useNavigate()
  const plan      = PRICING[(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
    if (tz.includes('Lagos') || tz.includes('Abuja'))  return 'NGN'
    if (tz.includes('London'))                          return 'GBP'
    if (tz.includes('Europe'))                          return 'EUR'
    return 'USD'
  })()] || PRICING.USD
  const [open, setOpen] = useState(null)

  return (
    <div style={{ background:'#FAFAF8', minHeight:'100vh' }}>
      <MarketingHeader/>

      {/* Hero */}
      <section className="pricing-hero" style={{ textAlign:'center', padding:'5rem 2rem 3rem', maxWidth:720, margin:'0 auto' }}>
        <div style={{ display:'inline-flex', alignItems:'center', gap:'0.4rem', background:'rgba(155,175,155,0.15)', border:'1px solid rgba(155,175,155,0.3)', borderRadius:100, padding:'0.35rem 1rem', marginBottom:'1.5rem' }}>
          <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#7A917A', letterSpacing:'0.06em' }}>SIMPLE PRICING</span>
        </div>
        <h1 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'clamp(2.2rem,5vw,3.5rem)', color:'#1A1A1A', margin:'0 0 1rem', lineHeight:1.15 }}>
          One price. Everything included.
        </h1>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'1.05rem', color:'#6B6B6B', lineHeight:1.8, margin:0 }}>
          No subscriptions. No monthly fees. Pay once and plan your entire wedding with every feature unlocked.
        </p>
      </section>

      {/* Pricing Cards */}
      <section className="pricing-cards-wrap" style={{ maxWidth:960, margin:'0 auto', padding:'2rem' }}>
        <div className="pricing-cards" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1.5rem', alignItems:'start' }}>

          {/* Free */}
          <div className="pricing-card" style={{ background:'white', border:'1.5px solid #E5E0D8', borderRadius:'1.5rem', padding:'2.5rem 2rem' }}>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', fontWeight:700, color:'#9BAF9B', letterSpacing:'0.1em', textTransform:'uppercase', margin:'0 0 0.75rem' }}>Free Plan</p>
            <div style={{ marginBottom:'0.5rem' }}>
              <span style={{ fontFamily:'Gibs, serif', fontSize:'3rem', color:'#1A1A1A', lineHeight:1 }}>Free</span>
            </div>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', color:'#6B6B6B', margin:'0 0 2rem' }}>No card required - Start today</p>
            <Link to="/signup" style={{ display:'block', textDecoration:'none', marginBottom:'2rem' }}>
              <button style={{ width:'100%', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.9rem', color:'#1A1A1A', background:'white', border:'2px solid #E5E0D8', borderRadius:100, padding:'0.875rem', cursor:'pointer' }}>
                Get Started Free
              </button>
            </Link>
            <div style={{ display:'flex', flexDirection:'column', gap:'0.625rem' }}>
              {FREE_FEATURES.map(f=>(
                <div key={f} style={{ display:'flex', gap:'0.625rem', alignItems:'center' }}>
                  <Check size={15} color="#9BAF9B" style={{ flexShrink:0 }}/>
                  <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', color:'#3A3A3A' }}>{f}</span>
                </div>
              ))}
              {FREE_LOCKED.map(f=>(
                <div key={f} style={{ display:'flex', gap:'0.625rem', alignItems:'center' }}>
                  <X size={15} color="#BCBCBC" style={{ flexShrink:0 }}/>
                  <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', color:'#BCBCBC' }}>{f}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Premium */}
          <div className="pricing-premium-card" style={{ background:'linear-gradient(145deg,#C4826A,#D4A882)', borderRadius:'1.5rem', padding:'2.5rem 2rem', position:'relative', boxShadow:'0 20px 60px rgba(196,130,106,0.3)' }}>
            <div style={{ position:'absolute', top:-14, left:'50%', transform:'translateX(-50%)', background:'#1A1A1A', borderRadius:100, padding:'0.3rem 1rem', display:'flex', alignItems:'center', gap:'0.4rem' }}>
              <Crown size={12} color="#F4B300"/>
              <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', fontWeight:700, color:'white', letterSpacing:'0.06em' }}>MOST POPULAR</span>
            </div>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', fontWeight:700, color:'rgba(255,255,255,0.7)', letterSpacing:'0.1em', textTransform:'uppercase', margin:'0 0 0.75rem' }}>Premium Plan</p>
            <div style={{ marginBottom:'0.5rem' }}>
              <span style={{ fontFamily:'Gibs, serif', fontSize:'3rem', color:'white', lineHeight:1 }}>{plan.symbol}{plan.amount.toLocaleString()}</span>
              <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', color:'rgba(255,255,255,0.7)', marginLeft:'0.5rem' }}>one-time</span>
            </div>

            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'rgba(255,255,255,0.6)', margin:'0 0 2rem' }}>Everything, forever</p>
            <div style={{ marginBottom:'2rem' }}>
              <UpgradeToPremium buttonLabel="Upgrade to Premium" onSuccess={()=>navigate('/dashboard')}/>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'0.625rem' }}>
              {PREM_FEATURES.map(f=>(
                <div key={f} style={{ display:'flex', gap:'0.625rem', alignItems:'center' }}>
                  <Check size={15} color="rgba(255,255,255,0.9)" style={{ flexShrink:0 }}/>
                  <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', color:'rgba(255,255,255,0.9)' }}>{f}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Diaspora note */}
        <div style={{ background:'white', border:'1px solid #E5E0D8', borderRadius:'1.25rem', padding:'1.5rem 2rem', marginTop:'1.5rem', display:'flex', gap:'0.875rem', alignItems:'flex-start' }}>
          <span style={{ fontSize:'1.5rem', flexShrink:0 }}>🌍</span>
          <div>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.9rem', color:'#1A1A1A', margin:'0 0 0.25rem' }}>Planning from anywhere in the world?</p>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', color:'#6B6B6B', margin:0, lineHeight:1.6 }}>
              Pay in your local currency - wherever you are. Payments processed securely via Paystack and Flutterwave.
            </p>
          </div>
        </div>

        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#BCBCBC', textAlign:'center', marginTop:'1rem' }}>
          Payments are processed securely by Paystack and Flutterwave. All sales are final. See our{' '}
          <Link to="/terms" style={{ color:'#C4826A', textDecoration:'none' }}>Terms of Service</Link> for details.
        </p>
      </section>

      {/* FAQ */}
      <section className="pricing-faq" style={{ padding:'4rem 2rem 5rem', maxWidth:720, margin:'0 auto' }}>
        <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'clamp(1.6rem,3vw,2.2rem)', color:'#1A1A1A', margin:'0 0 2rem', textAlign:'center' }}>Common Questions</h2>
        <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
          {PRICING_FAQS.map((faq,i)=>(
            <FAQItem key={i} faq={faq} isOpen={open===i} onToggle={()=>setOpen(open===i?null:i)}/>
          ))}
        </div>
      </section>

      <MarketingFooter/>

      <style>{`
        @media (max-width:768px) {
          .pricing-cards { grid-template-columns: 1fr !important; }
          .pricing-hero   { padding: 3rem 1.25rem 2rem !important; }
          .pricing-cards-wrap { padding: 1rem 1.25rem !important; }
          .pricing-faq    { padding: 3rem 1.25rem !important; }
          .pricing-card   { padding: 2rem 1.5rem !important; }
          .pricing-premium-card { padding: 2rem 1.5rem !important; }
        }
      `}</style>
    </div>
  )
}
